import { FC, useEffect, useState } from 'react';

import User from '@/assets/user.svg?react';

import { useFormDataQuery } from '@/api/getters';
import Home from '@/assets/home.svg?react';
import { Hint } from '@/components/ui/Hint';
import CheckboxAccountIsOwner, {
  OWNER_ACCOUNT_IS_OWNER_NAME,
} from '@/features/v2/summaryForms/components/CheckboxAccountIsOwner';
import CheckboxWithoutPESEL from '@/features/v2/summaryForms/components/CheckboxWithoutPESEL';
import CheckboxWithoutStreet from '@/features/v2/summaryForms/components/CheckboxWithoutStreet';
import InputAddress from '@/features/v2/summaryForms/components/InputAdress';
import InputBuildingNumber from '@/features/v2/summaryForms/components/InputBuildingNumber';
import InputCity from '@/features/v2/summaryForms/components/InputCity';
import InputEmail from '@/features/v2/summaryForms/components/InputEmail';
import InputFirstName from '@/features/v2/summaryForms/components/InputFirstName';
import InputFlatNumber from '@/features/v2/summaryForms/components/InputFlatNumber';
import InputLastName from '@/features/v2/summaryForms/components/InputLastName';
import InputPesel from '@/features/v2/summaryForms/components/InputPesel';
import InputPhone from '@/features/v2/summaryForms/components/InputPhone';
import InputPostalCode from '@/features/v2/summaryForms/components/InputPostalCode';

import { useLingui } from '@lingui/react';
import { useFormContext } from 'react-hook-form';

interface Props {
  prefix?: 'owner' | `coowners.${number}`;
  index?: number;
  defaultValues: {
    phone: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

const CompaniesPersonInputs: FC<Props> = ({
  prefix = 'owner',
  index,
  defaultValues,
}) => {
  const isCowner = prefix !== 'owner';
  const { i18n } = useLingui();

  const [focused, setFocused] = useState('');
  const [theSame] = useState(false);
  const { trigger, watch } = useFormContext();
  const { data } = useFormDataQuery();

  useEffect(() => {
    const fields = [
      'first_name',
      'last_name',
      'pesel',
      'phone',
      'email',
      'address',
      'building_number',
      'zip_code',
      'city',
    ];

    fields.forEach((field) => {
      if (data?.payload?.data?.owner) {
        if (data?.payload?.data[prefix]?.[field]?.length > 0) {
          trigger(`${prefix}.${field}`);
        }
      }
    });

    if (index !== undefined && index !== null) {
      const coownersFields = [...fields];

      coownersFields.forEach((field) => {
        if (data?.payload?.data?.coowners?.[index]?.[field]?.length > 0) {
          trigger(`${prefix}.${field}`);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAccountOwner = watch(OWNER_ACCOUNT_IS_OWNER_NAME);

  return (
    <section>
      <div
        className={
          'mt-3 flex flex-col gap-5 rounded-md bg-gray-200 p-3.5 md:p-6'
        }
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <p className="h5 col-span-4 flex items-center gap-2">
            <User className="size-4 text-blue-600" />
            {i18n.t(`Reprezentant spółki `)}(
            {prefix === 'owner' ? 1 : Number(index) + 2})
          </p>
          {!isCowner && (
            <CheckboxAccountIsOwner
              label={i18n.t('Jestem reprezentantem spółki')}
              accountPhoneValue={defaultValues.phone}
              accountEmailValue={defaultValues.email}
              accountFirstNameValue={defaultValues.first_name}
              accountLastNameValue={defaultValues.last_name}
            />
          )}
          {isCowner && (
            <Hint icon="bulb" color="blue" className="col-span-4">
              {i18n.t(
                'Zwróć uwagę, aby numer telefonu i adres e-mail był właściwy dla każdego reprezentanta. Podaj dane osoby upoważnionej do reprezentowania spółki zgodnie z KRS. Jeśli jest ich więcej niż jedna, dodaj kolejnych reprezentantów.'
              )}
            </Hint>
          )}
          {!isCowner && (
            <Hint icon="bulb" color="blue" className="col-span-4">
              {i18n.t(
                'Podaj dane osoby upoważnionej do reprezentowania spółki zgodnie z KRS.'
              )}
              <br />
              {i18n.t(
                'Jeśli jest ich więcej niż jedna, dodaj kolejnych reprezentantów.'
              )}
            </Hint>
          )}
          <InputFirstName
            name={`${prefix}.first_name`}
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
          />
          <InputLastName
            name={`${prefix}.last_name`}
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
          />
          <InputPhone
            name={`${prefix}.phone`}
            focused={focused}
            index={index ?? 0}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
            disabled={isAccountOwner && !isCowner}
          />
          <InputEmail
            name={`${prefix}.email`}
            focused={focused}
            index={index ?? 0}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
            disabled={isAccountOwner && !isCowner}
          />{' '}
          <InputPesel
            name={`${prefix}.pesel`}
            focused={focused}
            setFocused={setFocused}
            disabled={!!watch(`${prefix}.without_pesel`)}
            index={index}
          />
          <CheckboxWithoutPESEL name={`${prefix}.without_pesel`} />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <p className="h5 flex items-center gap-2 md:col-span-4">
            <Home className="size-4 shrink-0 text-blue-600" />
            {i18n.t('Adres zamieszkania')}
          </p>
          {/* {isCowner && (
            <TheSameAddressCheckbox
              recipientType={BUSINESS_TYPE.ZOO}
              index={index}
              theSame={theSame}
              setTheSame={setTheSame}
              prefix={prefix}
            />
          )} */}
          <InputPostalCode
            name={`${prefix}.zip_code`}
            controlled
            focused={focused}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
            prefix={prefix}
            disabled={
              (isAccountOwner && !isCowner && data?.user?.postal_code) ||
              theSame
            }
          />
          <InputCity
            name={`${prefix}.city`}
            controlled
            focused={focused}
            disabled={theSame}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
            prefix={prefix}
          />
          <CheckboxWithoutStreet prefix={prefix} disabled={theSame} />
          <InputAddress
            name={`${prefix}.address`}
            controlled
            creatable
            focused={focused}
            disabled={theSame}
            setFocused={setFocused}
            autoCompleteOff={isCowner}
            prefix={prefix}
          />
          <div className="col-span-4 grid grid-cols-4 gap-3">
            <InputBuildingNumber
              name={`${prefix}.building_number`}
              focused={focused}
              disabled={theSame}
              setFocused={setFocused}
              autoCompleteOff={isCowner}
            />
            <InputFlatNumber
              name={`${prefix}.flat_number`}
              focused={focused}
              disabled={theSame}
              setFocused={setFocused}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompaniesPersonInputs;
